import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, LogOut, Plus, Archive, ArchiveRestore, ExternalLink, ArrowUpRight, Trash2 } from "lucide-react";
import { api, authHeader, getAdminToken, setAdminToken, clearAdminToken } from "@/lib/api";

function formatErr(detail) {
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && e.msg) || JSON.stringify(e)).join(" ");
  return "Something went wrong";
}

const Login = ({ onLogin }) => {
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!password) return;
    setSubmitting(true);
    try {
      const res = await api.post("/admin/login", { password });
      setAdminToken(res.data.token);
      onLogin();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || "Login failed");
    } finally {
      setSubmitting(false);
    }
  };
  return (
    <main className="min-h-screen flex items-center justify-center px-6" style={{ background: "var(--senk-bg)", color: "var(--senk-fg)" }}>
      <div className="w-full max-w-md senk-rise">
        <p className="label-eyebrow">Studio Access</p>
        <h1 className="font-serif-display text-5xl mt-4 leading-tight">Sign in.</h1>
        <p className="mt-4" style={{ color: "var(--senk-body)" }}>Enter the studio password to manage your projects.</p>
        <form onSubmit={submit} className="mt-12">
          <label className="label-eyebrow">Password</label>
          <input type="password" data-testid="admin-password-input" value={password} onChange={(e) => setPassword(e.target.value)} autoFocus className="senk-input mt-3" placeholder="••••••••" />
          <button type="submit" disabled={submitting} data-testid="admin-login-submit" className="btn-primary btn-type mt-10 w-full justify-center">
            {submitting ? <Loader2 size={16} className="animate-spin" /> : <ArrowUpRight size={16} strokeWidth={2} />}
            <span>Enter Studio</span>
          </button>
        </form>
      </div>
    </main>
  );
};

const ProjectList = ({ onLogout }) => {
  const nav = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showArchived, setShowArchived] = useState(false);
  const [newName, setNewName] = useState("");
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/projects", { headers: authHeader() });
      setProjects(res.data);
    } catch (err) {
      if (err.response?.status === 401) { onLogout(); return; }
      toast.error("Failed to load projects");
    } finally { setLoading(false); }
  }, [onLogout]);

  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    setCreating(true);
    try {
      const res = await api.post("/admin/projects", { name: newName.trim() }, { headers: authHeader() });
      setNewName("");
      toast.success("Project created");
      nav(`/admin/projects/${res.data.id}`);
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || "Failed to create");
    } finally { setCreating(false); }
  };

  const toggleArchive = async (p) => {
    try {
      await api.post(`/admin/projects/${p.id}/archive`, { archived: !p.archived }, { headers: authHeader() });
      toast.success(p.archived ? "Restored" : "Archived");
      load();
    } catch { toast.error("Action failed"); }
  };

  const deleteProject = async (p) => {
    if (!window.confirm(`Permanently delete "${p.name}"? This cannot be undone. All milestones, feedback, testimonials and media will be removed.`)) return;
    try {
      await api.delete(`/admin/projects/${p.id}`, { headers: authHeader() });
      toast.success("Project deleted");
      load();
    } catch { toast.error("Delete failed"); }
  };

  const portalUrl = (slug) => `${window.location.origin}/portal/${slug}`;
  const visible = projects.filter((p) => showArchived ? p.archived : !p.archived);

  return (
    <main className="min-h-screen" style={{ background: "var(--senk-bg)", color: "var(--senk-fg)" }}>
      <header style={{ borderBottom: "0.5px solid var(--senk-border)" }}>
        <div className="max-w-6xl mx-auto px-6 md:px-12 py-6 flex items-center justify-between">
          <div className="flex flex-col">
            <span className="label-eyebrow">Studio · Projects</span>
            <span className="brand-mark mt-1">SENK DESIGN</span>
          </div>
          <button onClick={onLogout} data-testid="admin-logout" className="btn-ghost btn-type">
            <LogOut size={14} strokeWidth={2} /> Sign out
          </button>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 md:px-12 py-16">
        <form onSubmit={create} className="flex flex-col sm:flex-row sm:items-end gap-4 max-w-3xl" data-testid="create-project-form">
          <div className="flex-1">
            <label className="label-eyebrow">New project</label>
            <input data-testid="new-project-name" value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Atelier Noir — FW26" className="senk-input mt-2" />
          </div>
          <button type="submit" disabled={creating} data-testid="create-project-submit" className="btn-primary btn-type">
            {creating ? <Loader2 size={16} className="animate-spin" /> : <Plus size={14} strokeWidth={2} />}
            <span>Create</span>
          </button>
        </form>

        <div className="flex items-center gap-6 mt-12 pb-3" style={{ borderBottom: "0.5px solid var(--senk-border)" }}>
          {[{id: false, label: "Active"}, {id: true, label: "Archived"}].map((t) => (
            <button key={String(t.id)} onClick={() => setShowArchived(t.id)} data-testid={t.id ? "tab-archived" : "tab-active"} className="label-eyebrow pb-2 transition-colors"
              style={{ borderBottom: showArchived === t.id ? "1.5px solid var(--senk-fg)" : "1.5px solid transparent",
                       color: showArchived === t.id ? "var(--senk-fg)" : "var(--senk-body)" }}>
              {t.label} ({projects.filter((p) => p.archived === t.id).length})
            </button>
          ))}
        </div>

        {loading ? (
          <div className="py-24 flex justify-center"><Loader2 className="animate-spin" size={24} /></div>
        ) : visible.length === 0 ? (
          <p className="mt-10" style={{ color: "var(--senk-body)" }}>No {showArchived ? "archived" : "active"} projects.</p>
        ) : (
          <ul className="mt-6" data-testid="project-list">
            {visible.map((p) => (
              <li key={p.id} className="py-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4"
                  style={{ borderBottom: "0.5px solid var(--senk-border)" }} data-testid="project-row">
                <div className="flex-1 min-w-0">
                  <button onClick={() => nav(`/admin/projects/${p.id}`)} data-testid={`project-open-${p.slug}`}
                          className="font-serif-display text-2xl md:text-3xl text-left hover:opacity-70 transition-opacity block truncate" style={{ color: "var(--senk-fg)" }}>
                    {p.brand_name ? <span className="label-eyebrow block mb-1" style={{ color: "var(--senk-body)" }}>{p.brand_name}</span> : null}
                    {p.name || "Untitled project"}
                  </button>
                  <p className="label-eyebrow mt-2">{p.timeline || "No timeline"} · /portal/{p.slug}</p>
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                  <a href={portalUrl(p.slug)} target="_blank" rel="noopener noreferrer" className="btn-ghost btn-type" data-testid={`project-link-${p.slug}`}>
                    <ExternalLink size={14} strokeWidth={2} /> Open Portal
                  </a>
                  <button onClick={() => toggleArchive(p)} className="btn-ghost btn-type" data-testid={`project-archive-${p.slug}`}>
                    {p.archived ? <><ArchiveRestore size={14} strokeWidth={2} /> Restore</> : <><Archive size={14} strokeWidth={2} /> Archive</>}
                  </button>
                  <button onClick={() => deleteProject(p)} className="btn-ghost btn-type" data-testid={`project-delete-${p.slug}`} style={{ color: "#B00020" }}>
                    <Trash2 size={14} strokeWidth={2} /> Delete
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </main>
  );
};

export default function Admin() {
  const [authed, setAuthed] = useState(!!getAdminToken());
  const [checking, setChecking] = useState(!!getAdminToken());
  useEffect(() => {
    if (!getAdminToken()) return;
    (async () => {
      try { await api.get("/admin/me", { headers: authHeader() }); setAuthed(true); }
      catch { clearAdminToken(); setAuthed(false); }
      finally { setChecking(false); }
    })();
  }, []);
  const logout = () => { clearAdminToken(); setAuthed(false); };
  if (checking) return <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--senk-bg)" }}><Loader2 className="animate-spin" size={24} /></div>;
  return authed ? <ProjectList onLogout={logout} /> : <Login onLogin={() => setAuthed(true)} />;
}
